import { useCallback, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { ModalDisplayedTracker } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Text from '@zenbusiness/zui/Text';

import { useCompletionsMutation } from '#client/hooks/useCompletionsMutation';
import { usePromptManualQuery } from '#client/hooks/usePromptManualQuery';

import { AssistantFooter } from './components/AssistantFooter';
import { AssistantInput } from './components/AssistantInput';
import { AssistantResponse } from './components/AssistantResponse';
import { SduCompliance } from './components/SduCompliance';
import { WelcomeView } from './components/WelcomeView';
import { WFC_UPSELL_CONFIG } from './constants';
import * as Styled from './VeloAIAssistant.styles';

export const LOCALIZED_TEXT = {
  headerTitle: 'businessProfile.aiAssistant.headerTitle',
  labelInputPrompt: 'businessProfile.aiAssistant.inputPrompt',
  errorMessage: 'businessProfile.aiAssistant.errorMessage',
};

function VeloAIAssistant({ eventingProperties, onClose, open, promptSlug, queryType, subtitle = '', title = '' }) {
  const { formatMessage: fm } = useIntl();
  const business = useBusinessEntity();
  const {
    businessEntity: { type, domesticJurisdiction, name },
    entitlements,
  } = business;

  const {
    loading: promptLoading,
    error: promptError,
    execute: fetchPrompt,
  } = usePromptManualQuery({ queryType, promptSlug });

  const {
    fetchCompletions,
    data: completionsData,
    loading: completionsLoading,
    error: completionsError,
  } = useCompletionsMutation({ queryType });

  const [userInput, setUserInput] = useState('');
  const [sentUserMessage, setSentUserMessage] = useState('');

  const parsedData = completionsData?.completions?.messages[0]?.parsed;
  const loading = promptLoading || completionsLoading;
  const hasError = promptError || completionsError;

  const isEmptyData = !parsedData && !loading && !hasError;
  const hasPositiveResponse = !isEmpty(parsedData?.output);

  const handleCompletionRequest = useCallback(
    async (searchTerm) => {
      setSentUserMessage(searchTerm);
      setUserInput('');
      const promptResult = await fetchPrompt(); // Cached after the first call
      const prompt = promptResult?.data;
      if (!prompt) {
        return;
      }

      fetchCompletions(prompt, {
        input: JSON.stringify({
          input: {
            search_term: searchTerm,
            state: domesticJurisdiction,
            entity_type: type,
          },
        }),
      });
    },
    [fetchCompletions, fetchPrompt, domesticJurisdiction, type],
  );

  const handleSendMessage = useCallback(() => {
    handleCompletionRequest(userInput);
  }, [handleCompletionRequest, userInput]);

  const handleClickQuickAction = useCallback(
    (incomingVal) => {
      const message = fm({ id: incomingVal });
      setSentUserMessage(message);
      handleCompletionRequest(message);
    },
    [handleCompletionRequest, fm],
  );

  const handleChangeInput = useCallback((e) => {
    setUserInput(e.target.value);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderMainContent = () => {
    if (hasError) {
      return <FormattedMessage id={LOCALIZED_TEXT.errorMessage} />;
    }

    if (loading || parsedData) {
      const { aiProjectName, ...rest } = eventingProperties;
      return (
        <>
          <AssistantResponse
            businessEntity={{
              domesticJurisdiction,
              name,
              type,
            }}
            hasPositiveResponse={hasPositiveResponse}
            loading={loading}
            onClickQuickAction={handleClickQuickAction}
            prompt={sentUserMessage}
            results={parsedData?.output}
          />
          <ModalDisplayedTracker
            dynamicProperties={{
              ai_project_name: aiProjectName,
            }}
            open
            {...rest}
          />
        </>
      );
    }

    return <WelcomeView onClickQuickAction={handleClickQuickAction} />;
  };

  return (
    <Styled.StyledDrawer
      title={isEmptyData && fm({ id: title })}
      overline={fm({ id: LOCALIZED_TEXT.headerTitle })}
      subtitle={
        subtitle &&
        isEmptyData && (
          <Text size="sm" sx={{ whiteSpace: 'pre-line' }} text="secondary">
            <FormattedMessage id={subtitle} />
          </Text>
        )
      }
      open={open}
      onClose={handleClose}
    >
      {/* Main content area */}
      <Styled.ContentContainer>{renderMainContent()}</Styled.ContentContainer>

      {/* Input area */}
      {(isEmptyData || (!hasPositiveResponse && parsedData)) && (
        <Styled.StyledInputContainer>
          <Text weight="medium" color="tertiary" size="sm">
            <FormattedMessage id={LOCALIZED_TEXT.labelInputPrompt} />
          </Text>
          <AssistantInput
            disabled={loading}
            onChange={handleChangeInput}
            onSend={handleSendMessage}
            value={userInput}
          />
        </Styled.StyledInputContainer>
      )}

      {hasPositiveResponse && <SduCompliance entitlements={entitlements} upsellConfig={WFC_UPSELL_CONFIG} />}

      {/* Footer area */}
      <AssistantFooter />
    </Styled.StyledDrawer>
  );
}

VeloAIAssistant.propTypes = {
  eventingProperties: PropTypes.shape({
    aiProjectName: PropTypes.string.isRequired,
    elementName: PropTypes.string.isRequired,
    elementText: PropTypes.string,
    elementValue: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  promptSlug: PropTypes.string,
  queryType: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

function VeloAIAssistantWrapper({ open = false, ...delegatedProps }) {
  if (!open) {
    return null;
  }

  return <VeloAIAssistant open={open} {...delegatedProps} />;
}

VeloAIAssistantWrapper.propTypes = {
  open: PropTypes.bool,
};

export default VeloAIAssistantWrapper;
